import {
  useContext, useEffect, useState,
} from 'react';

import { OptimizelyContext } from '@optimizely/react-sdk';

import { OptimizelyFeatureFlagT, OptimizelyOptionsT } from '@consumer-frontend/nc-utils';

import { useOptimizelyFeature } from './useOptimizelyFeature';

const OPTIMIZELY_READY_TIMEOUT_MS = 3000;

export const useOptimizelyFeatureEnabledWhenReady = (
  featureKey: OptimizelyFeatureFlagT,
  options?: OptimizelyOptionsT,
): { isEnabled: boolean; isLoading: boolean; } => {
  const { optimizely } = useContext(OptimizelyContext);
  const isEnabled = useOptimizelyFeature(featureKey, options);

  // `hasOptimizelyDataFileResolved` tracks whether we're done waiting on Optimizely's datafile fetch (successfully, or via timeout/error)
  // When there's no `optimizely` client at all (e.g. no provider in context), there is no datafile fetch to wait for,
  // so we resolve immediately rather than loading forever and `isEnabled` will simply default to `false` in that case.
  const [hasOptimizelyDataFileResolved, setHasOptimizelyDataFileResolved] = useState(!optimizely);

  // Waits for the Optimizely client to finish fetching its datafile (or time out) before marking the
  // flag as resolved, so callers can show a loading state until a real value is available.
  useEffect(
    () => {
      // For testing and storybook environments where there is no optimizely client to wait on
      if (!optimizely) {
        setHasOptimizelyDataFileResolved(true);

        return undefined;
      }

      // Guards against calling setState after this effect's cleanup has run - e.g. if the component
      // unmounts, or `optimizely` changes and this effect re-runs, before `onReady()` settles.
      let isCurrentEffect = true;

      const markResolved = (): void => {
        if (isCurrentEffect) {
          setHasOptimizelyDataFileResolved(true);
        }
      };

      optimizely.onReady({ timeout: OPTIMIZELY_READY_TIMEOUT_MS }).then(markResolved).catch(markResolved);

      return () => {
        isCurrentEffect = false;
      };
    },
    [optimizely],
  );

  return {
    isEnabled,
    isLoading: !hasOptimizelyDataFileResolved,
  };
};
